import { makeAutoObservable, observable, runInAction } from 'mobx';
import { AUTH_API } from 'src/config';
import { Storage } from 'src/storage';
import { resetStores } from './resetStores';
import { PreferencesStore } from './PreferencesStore';
import { LocalizationService } from '../services/LocalizationService';
import { decodeJwt, isTokenExpired } from '../services/jwt';

interface UserData {
  id: number;
  name: string;
  role?: number;
  groupId?: number;
  masterUser: boolean;
  locale?: string;
}

type State = {
  token: string | null;
  isAuthenticated: boolean;
  userData: UserData | null;
};

class SessionStoreClass {
  private _state: State;

  constructor() {
    // Initialize state BEFORE makeAutoObservable to ensure proper loading
    this._state = observable({
      token: Storage.get('token'),
      isAuthenticated: !!Storage.get('token'),
      userData: this.loadUserData(),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  private loadUserData(): UserData | null {
    // Try to load user data from localStorage (for tests and cached sessions)
    // If not found, will be fetched from /auth/session on startup
    const storedUserData = Storage.get('userData');
    if (storedUserData) {
      return storedUserData as UserData;
    }
    return null;
  }

  get token() {
    return this._state.token;
  }

  get isAuthenticated() {
    return this._state.isAuthenticated;
  }

  get userData() {
    return this._state.userData;
  }

  get isMasterUser(): boolean {
    return this._state.userData?.masterUser || false;
  }

  get isAdmin(): boolean {
    return this.isMasterUser || this._state.userData?.role === 1;
  }

  setToken(token: string, userData?: UserData) {
    runInAction(() => {
      this._state.token = token;
      this._state.isAuthenticated = true;
      if (userData) {
        this._state.userData = userData;
      }
    });

    Storage.set('token', token);
    // Do NOT store userData in localStorage anymore
  }

  clearToken() {
    runInAction(() => {
      this._state.token = null;
      this._state.isAuthenticated = false;
      this._state.userData = null;
    });
    Storage.remove('token');
    Storage.remove('userRole');
  }

  async login(
    username: string,
    password: string,
    code?: string
  ): Promise<{
    success: boolean;
    requires2FA?: boolean;
    phoneNumber?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${AUTH_API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login: username,
          password,
          ...(code && { code }),
        }),
      });

      if (!response.ok) {
        return { success: false, error: 'Invalid credentials' };
      }

      const data = await response.json();

      // Check if 2FA is required
      if (data.requires2FA) {
        return {
          success: false,
          requires2FA: true,
          phoneNumber: data.phoneNumber,
        };
      }

      // Login successful with token
      if (data.accessToken) {
        // Decode remote token to get basic user data
        const payload = decodeJwt(data.accessToken);
        if (!payload) {
          return { success: false, error: 'Invalid token' };
        }

        const userData: UserData = {
          id: payload.user?.id || payload.sub,
          name: payload.name,
          masterUser: !!payload.masterUser || !!payload.user,
          groupId: payload.groupId,
          role: data.role,
          locale: data.locale,
        };

        // A login reached without a logout first — /login typed straight into
        // the address bar — would otherwise inherit the previous user's data.
        resetStores();

        // Store remote token (needed for V1 API calls)
        this.setToken(data.accessToken, userData);

        // Persist role so it survives page reload (not in JWT, not returned by /auth/session)
        if (userData.role !== undefined) {
          Storage.set('userRole', userData.role);
        }

        // Load frontend preferences (theme, view modes, etc.) from localStorage.
        // User settings (locale, timezone) are owned by UserStore / remote API.
        await PreferencesStore.initialize();
        // Apply locale from login response and persist it so page reload keeps the locale.
        const loginLocale = data.locale || PreferencesStore.locale;
        LocalizationService.applyLocale(loginLocale);
        if (data.locale) PreferencesStore.locale = data.locale;
        return { success: true };
      }

      return { success: false, error: 'No token received' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  /**
   * Fetch session data from backend using stored token.
   * Called on app startup to restore user data.
   */
  async fetchSession(): Promise<boolean> {
    if (!this._state.token) return false;

    // The backend has no session endpoint; the token itself carries the user.
    return this.decodeTokenClientSide();
  }

  /**
   * Decode JWT token client-side to restore user data.
   * Fallback when /auth/session is unavailable.
   */
  private decodeTokenClientSide(): boolean {
    const token = this._state.token!;
    const payload = decodeJwt(token);
    if (!payload || isTokenExpired(token)) {
      console.error('Stored token is unreadable or expired');
      this.clearToken();
      return false;
    }

    const storedRole = Storage.get('userRole');
    const userData: UserData = {
      id: payload.user?.id || payload.sub,
      name: payload.name,
      masterUser: !!payload.masterUser || !!payload.user,
      groupId: payload.groupId,
      role: payload.role ?? (storedRole !== null ? (storedRole as number) : undefined),
    };

    runInAction(() => {
      this._state.userData = userData;
      this._state.isAuthenticated = true;
    });

    return true;
  }

  async logout(): Promise<void> {
    const token = this._state.token;
    if (token) {
      fetch(`${AUTH_API}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken: token }),
      }).catch(() => {});
    }
    this.clearToken();
    // Logout navigates with the router, so the singleton stores survive it;
    // without this the next login reads the previous user's devices.
    resetStores();
    PreferencesStore.reset();
  }
}

export const SessionStore = new SessionStoreClass();
